import { FunctionComponent, useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Group } from '@/types/Group'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import GroupRow from './groupTableRow'
import styles from './group.module.scss'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { GET_GROUPS } from '@/modules/API/daycare/groupHandler'
import Title from '@/components/_custom/Title/title'
import Loading from '@/components/Loading/Loading'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import GroupDialog from './Dialog/groupDialog'
import { useAuth } from '@/modules/context/AuthContext'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import { useDaycareRight } from '@/hooks/useDaycareRight'

const Groups: FunctionComponent = () => {
    const { t } = useTranslation()

    const { currentDaycare: daycare } = useCurrentDaycareInfos()

    const canUpdate: boolean = useDaycareRight('canWriteParamsDaycare')

    const { fetchCurrentDaycare } = useAuth()

    const [groupsList, setGroupsList] = useState<Array<Group>>([])

    const [isGroupDialogOpen, setGroupDialogOpen] = useState<boolean>(false)

    const {
        loading: loadingGroups,
        error: errorGroups,
        data: dataGroups
    } = useQuery<{ getGroups: Group[] }>(GET_GROUPS, {
        fetchPolicy: 'network-only',
        variables: {
            daycare_id: daycare.id
        }
    })

    useEffect(() => {
        if (dataGroups && dataGroups.getGroups.length > 0) {
            setGroupsList([...dataGroups.getGroups].sort((a: Group, b: Group) => a.name.localeCompare(b.name)))
        } else {
            setGroupsList([])
        }
    }, [dataGroups])

    function addOneGroup(group: Group): void {
        const newGroup: Group[] = [...groupsList, group].sort((a: Group, b: Group) => a.name.localeCompare(b.name))
        setGroupsList([])
        setGroupsList(newGroup)
        fetchCurrentDaycare()
    }

    function removeOneGroup(groupId: number): void {
        const groups: Group[] = groupsList.filter((group: Group): boolean => group.id !== groupId)
        setGroupsList([])
        setGroupsList(groups)
        fetchCurrentDaycare()
    }

    function updateOneGroup(group: Group): void {
        const groupList: Group[] = groupsList.filter((g: Group): boolean => g.id !== group.id)
        setGroupsList([])
        setGroupsList([...groupList, group].sort((a: Group, b: Group) => a.name.localeCompare(b.name)))
        fetchCurrentDaycare()
    }

    return (
        <div className={styles.GroupPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('myPriorityGroup')} className={styles.Title} />
                    {canUpdate && (
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setGroupDialogOpen(true)}
                        />
                    )}
                </div>
                {loadingGroups && <Loading overall={false} />}
                {dataGroups && (
                    <>
                        <TableContainer className={styles.TablePlacement}>
                            <Table className={styles.Table}>
                                <TableHead className={styles.TableHeader}>
                                    <TableRow>
                                        <TableCell sx={{ width: '40%' }}>{t('name')}</TableCell>
                                        {canUpdate && (
                                            <TableCell colSpan={2} sx={{ width: '10%' }}>
                                                {t('actions')}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {groupsList.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3}>{t('noPriorityGroup')}</TableCell>
                                        </TableRow>
                                    ) : (
                                        groupsList.map((group: Group, index: number) => (
                                            <GroupRow
                                                key={group.name + index}
                                                group={group}
                                                daycare={daycare}
                                                canUpdate={canUpdate}
                                                removeOneGroup={removeOneGroup}
                                                updateOneGroup={updateOneGroup}
                                            />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <GroupDialog
                            open={isGroupDialogOpen}
                            daycare={daycare}
                            handleClose={() => setGroupDialogOpen(false)}
                            create={true}
                            addOneGroup={addOneGroup}
                        />
                    </>
                )}
                {errorGroups && <ErrorMessage error={t('generalError')} />}
            </div>
        </div>
    )
}

export default Groups
